import { useMemo, useState, useRef, useEffect } from "react";
import {
  Box,
  Grid,
  IconButton,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
} from "@mui/material";
import { RouteIcon, makeSxStyles } from "ui";
import {
  GroupOutlined,
  FlagOutlined,
  MapOutlined,
  NotificationsOutlined,
  ManageAccountsOutlined,
  PersonAddAltOutlined,
} from "@mui/icons-material";
import { FleetManagementIcon } from "../../../components/snowplow";
import ManageOrganizationsIcon from "../../../shared/components/sidebar/ManageOrganizationsIcon";
import { useLocation, useNavigate } from "react-router-dom";
import { AdRoutes } from "../../../constants/ad-routes";
import merge from "lodash/merge";
import { DateTime } from "luxon";
import {
  useAdminLocationControllerGetCsvUploadState,
  useAdminLocationControllerCancelCsvUpload,
} from "service";
import { toast } from "react-toastify";
import useCsvUploadStore from "../../../state/csv-upload-store";
import { CustomerManagementIcon } from "../../../shared/components/sidebar/CustomerManagementIcon";
import useRole from "../../../hooks/use-role";
import useTopOffset from "../../../hooks/use-top-offset";

const notifications_enabled =
  import.meta.env.VITE_NOTIFICATIONS_ENABLED === "true";

const leads_enabled = import.meta.env.VITE_LEADS_ENABLED === "true";

const Sidebar = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { isSuperAdmin } = useRole();
  const topOffset = useTopOffset();

  const [open, setOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [isTooltipHovered, setIsTooltipHovered] = useState(false);
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const autoHideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    showTooltip: showUploadTooltip,
    status: uploadStatus,
    modalStatus,
    wasCancelled,
    hideTooltip,
    cancelUpload,
    startUpload,
    setReadyForReview,
    setApplyingChanges,
    resumeProgressUpdates,
  } = useCsvUploadStore();

  useEffect(() => {
    if (showUploadTooltip && !isTooltipHovered) {
      if (autoHideTimeoutRef.current) {
        clearTimeout(autoHideTimeoutRef.current);
      }

      autoHideTimeoutRef.current = setTimeout(() => {
        hideTooltip();
        autoHideTimeoutRef.current = null;
      }, 5000);
    } else if (!showUploadTooltip || isTooltipHovered) {
      if (autoHideTimeoutRef.current) {
        clearTimeout(autoHideTimeoutRef.current);
        autoHideTimeoutRef.current = null;
      }
    }
  }, [showUploadTooltip, isTooltipHovered, hideTooltip]);

  const { data: csvUploadState, refetch: refetchUploadState } =
    useAdminLocationControllerGetCsvUploadState({});

  useEffect(() => {
    if (modalStatus === "success") {
      refetchUploadState();
    }
  }, [modalStatus]);

  const {
    mutate: cancelCsvUploadInProgress,
    isLoading: isCancellingInProgress,
  } = useAdminLocationControllerCancelCsvUpload({
    onSuccess: () => {
      toast.success("Upload cancelled successfully");
      cancelUpload();
    },
    onError: () => {
      toast.error("Failed to cancel upload. Please try again.");
    },
  });

  const handleConfirmCancel = () => {
    setCancelDialogOpen(false);

    cancelUpload();
    cancelCsvUploadInProgress({});
  };

  const handleKeepUploading = () => {
    setCancelDialogOpen(false);

    resumeProgressUpdates();

    if (csvUploadState?.data) {
      const uploadState = csvUploadState.data;

      if (uploadState.isProcessing) {
        const tmpData = uploadState.tmpLocationCustomer;
        const hasChangesDetected =
          tmpData &&
          (tmpData.newAddressAndCustomer > 0 ||
            tmpData.existingAddressNewCustomer > 0 ||
            tmpData.updateCustomerOnExistingAddress > 0 ||
            tmpData.updateAddressOfExistingCustomer > 0 ||
            tmpData.existingCustomerNewAddress > 0);

        if (hasChangesDetected) {
          setReadyForReview(
            {
              ...tmpData,
              validationError: 0,
              validationErrors: [],
            },
            "Customer Upload Ready",
          );
        }
      }
    }
  };

  const clearTooltipTimeout = () => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    if (autoHideTimeoutRef.current) {
      clearTimeout(autoHideTimeoutRef.current);
      autoHideTimeoutRef.current = null;
    }
  };

  const handleOtherItemHover = () => {
    clearTooltipTimeout();
    setOpen(true);
  };

  useEffect(() => {
    if (open) {
      clearTooltipTimeout();
    }
  }, [open]);

  useEffect(() => {
    if (uploadStatus === "idle") {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
        tooltipTimeoutRef.current = null;
      }
      setIsTooltipHovered(false);
    }
  }, [uploadStatus]);

  useEffect(() => {
    if (csvUploadState?.data) {
      const uploadState = csvUploadState.data;

      if (wasCancelled) {
        return;
      }

      if (uploadState.isProcessing) {
        const tmpData = uploadState.tmpLocationCustomer;
        const hasChangesDetected =
          tmpData &&
          (tmpData.newAddressAndCustomer > 0 ||
            tmpData.existingAddressNewCustomer > 0 ||
            tmpData.updateCustomerOnExistingAddress > 0 ||
            tmpData.updateAddressOfExistingCustomer > 0 ||
            tmpData.existingCustomerNewAddress > 0);

        // Check if user has accepted and backend is applying changes
        if (uploadState.hasAccepted && uploadStatus !== "applying-changes") {
          setApplyingChanges("Applying your changes");
        } else if (
          hasChangesDetected &&
          uploadStatus !== "ready-for-review" &&
          uploadStatus !== "applying-changes" && // Don't override applying-changes state
          !uploadState.hasAccepted
        ) {
          // Only set review state if we haven't just completed an upload
          // This prevents the sidebar from immediately re-setting review state after completion
          const currentTime = Date.now();
          const lastCompleteTime = localStorage.getItem(
            "csvUploadCompleteTime",
          );
          const timeSinceComplete = lastCompleteTime
            ? currentTime - parseInt(lastCompleteTime)
            : Infinity;

          if (timeSinceComplete > 2000) {
            // Wait 2 seconds after completion
            setReadyForReview(
              {
                ...tmpData,
                validationError: 0,
                validationErrors: [],
              },
              "Customer Upload Ready",
            );
          }
        } else if (
          !hasChangesDetected &&
          uploadStatus !== "processing" &&
          uploadStatus !== "applying-changes" && // Don't override applying-changes state
          !uploadState.hasAccepted
        ) {
          startUpload("Processing Customer Upload");
        }
      } else {
        if (showUploadTooltip) {
          return;
        }

        if (
          uploadStatus === "processing" ||
          uploadStatus === "ready-for-review" ||
          uploadStatus === "applying-changes"
        ) {
          hideTooltip();
        }
      }
    }
  }, [
    csvUploadState,
    uploadStatus,
    wasCancelled,
    showUploadTooltip,
    startUpload,
    setReadyForReview,
    setApplyingChanges,
    hideTooltip,
  ]);

  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
      if (autoHideTimeoutRef.current) {
        clearTimeout(autoHideTimeoutRef.current);
      }
    };
  }, []);

  const sidebarLinks = useMemo(() => {
    return [
      {
        icon: <RouteIcon style={{ flexShrink: 0 }} />,
        text: "Route Planner",
        path: AdRoutes.routePlanner,
        param: ":date",
        paramValue: DateTime.now().toFormat("MM-dd-yyyy"),
        show: true,
      },
      {
        icon: <GroupOutlined />,
        text: "Customer Management",
        path: AdRoutes.customer,
        show: true,
      },
      {
        icon: <PersonAddAltOutlined />,
        text: "Leads",
        path: AdRoutes.leads,
        show: leads_enabled,
      },
      {
        icon: <MapOutlined />,
        text: "Region Management",
        path: AdRoutes.regionManagement,
        show: true,
      },
      {
        icon: <FleetManagementIcon />,
        text: "Fleet Management",
        path: AdRoutes.fleetManagement,
        show: true,
      },
      {
        icon: <ManageAccountsOutlined />,
        text: "Driver Management",
        path: AdRoutes.driverManagement,
        show: true,
      },
      {
        icon: <FlagOutlined />,
        text: "Snow Reports",
        path: AdRoutes.snowReports,
        show: true,
      },
      {
        icon: <NotificationsOutlined />,
        text: "Notification Center",
        path: AdRoutes.notifications,
        show: notifications_enabled,
      },
    ];
  }, []);

  const bottomLinks = useMemo(() => {
    return [
      {
        icon: <ManageOrganizationsIcon />,
        text: "Manage Organizations",
        path: AdRoutes.organizations,
        show: isSuperAdmin,
      },
    ];
  }, [isSuperAdmin]);

  return (
    <Grid
      item
      sx={merge(
        styles.container,
        { height: `calc(100vh - ${topOffset}px)` },
        open && styles.containerOpen,
      )}
      paddingX="8px"
      marginTop={`${topOffset}px`}
      onMouseLeave={() => setOpen(false)}
    >
      <Box sx={styles.stickyMenu}>
        {sidebarLinks.map((link) => {
          if (!link.show) return null;

          // Determine if this item should expand menu on hover
          const shouldExpandOnHover = !(
            link.text === "Customer Management" && showUploadTooltip
          );

          // Check if this is Customer Management with upload state
          const isCustomerManagement = link.text === "Customer Management";

          // Handle Customer Management icon separately to prevent cross-contamination
          if (isCustomerManagement) {
            return (
              <CustomerManagementIcon
                key={link.path}
                link={link}
                open={open}
                isTooltipHovered={isTooltipHovered}
                setIsTooltipHovered={setIsTooltipHovered}
                tooltipTimeoutRef={tooltipTimeoutRef}
                autoHideTimeoutRef={autoHideTimeoutRef}
                setCancelDialogOpen={setCancelDialogOpen}
                isCancellingInProgress={isCancellingInProgress}
                onSidebarStateChange={(isOpen) => {
                  // Force tooltip repositioning when sidebar state changes
                  if (!isOpen && isTooltipHovered) {
                    setIsTooltipHovered(false);
                  }
                }}
              />
            );
          }

          // Render normal sidebar icon (not Customer Management)
          return (
            <IconButton
              key={link.path}
              className={location.pathname.includes(link.path) ? "active" : ""}
              sx={merge(
                styles.sidebarItem,
                location.pathname.includes(link.path) &&
                  styles.activeSidebarItem,
              )}
              onClick={() => {
                const path =
                  link.param && link.paramValue
                    ? link.path.replace(link.param, link.paramValue)
                    : link.path;

                navigate(
                  path,
                  link.text === "Route Planner"
                    ? { state: { resolveRoutePlannerDate: true } }
                    : undefined,
                );
              }}
              onMouseEnter={
                shouldExpandOnHover ? handleOtherItemHover : undefined
              }
              data-testid={link.text}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {link.icon}
              </Box>
              {open && (
                <Typography sx={styles.buttonText} variant="body1">
                  {link.text}
                </Typography>
              )}
            </IconButton>
          );
        })}
      </Box>

      <Box sx={styles.bottomMenu}>
        {bottomLinks.map((link) => {
          if (!link.show) return null;

          return (
            <IconButton
              key={link.path}
              className={location.pathname.includes(link.path) ? "active" : ""}
              sx={merge(
                styles.sidebarItem,
                location.pathname.includes(link.path) &&
                  styles.activeSidebarItem,
              )}
              onClick={() => navigate(link.path)}
              onMouseEnter={handleOtherItemHover}
              data-testid={link.text}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {link.icon}
              </Box>
              {open && (
                <Typography sx={styles.buttonText} variant="body1">
                  {link.text}
                </Typography>
              )}
            </IconButton>
          );
        })}
      </Box>

      {/* Cancel Upload Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={handleKeepUploading}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="titleLarge">Cancel Customer Upload?</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="bodyMedium">
            If you cancel now, no customers will be added.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px 24px" }}>
          <Button onClick={handleKeepUploading} variant="text">
            Keep uploading
          </Button>
          <Button onClick={handleConfirmCancel} variant="contained">
            Cancel upload
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

const useStyles = makeSxStyles(
  ({
    palette: {
      background: { paper },
      primary: { main, dark },
    },
  }) => ({
    container: {
      background: paper,
      position: "fixed",
      zIndex: 1003,
      maxWidth: "250px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      height: "calc(100vh - 56px)",
      width: "64px",
      transition: "width 0.2s ease-in-out",
    },
    containerOpen: {
      width: "250px",
    },
    sidebarItem: {
      padding: "12px",
      mb: "12px",
      borderRadius: "24px",
      gap: "12px",
      justifyContent: "flex-start",
    },
    activeSidebarItem: {
      "&.active": {
        background: dark,
      },
    },
    stickyMenu: {
      position: "sticky",
      top: 0,
      display: "flex",
      flexDirection: "column",
      width: "100%",
      alignItems: "stretch",
      paddingTop: "24px",
    },
    bottomMenu: {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      alignItems: "stretch",
      paddingBottom: "24px",
    },
    button: {
      width: "50px",
      height: "50px",
    },
    icon: {
      width: "25px",
      height: "25px",
    },
    activeButton: {
      background: main,
      color: "black",
    },
    buttonText: {
      whiteSpace: "nowrap",
      overflow: "hidden",
    },
    uploadTooltip: {
      padding: "14px",
      minWidth: "310px",
      maxWidth: "310px",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    uploadTooltipProgress: {
      height: "4px",
    },
    uploadTooltipFooter: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
  }),
);

export default Sidebar;
